import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { projectApi, Project } from '../services/projectApi';
import { attendanceApi } from '../services/attendanceApi';
import Holidays from 'date-holidays';
import NewProjectModal from '../components/NewProjectModal';

interface CalendarEvent {
  id: number;
  title: string;
  startDate: Date;
  endDate: Date;
  type: 'PROJECT' | 'HOLIDAY' | 'LEAVE';
  status?: string;
  client?: string;
  pm?: string;
  project?: Project;
  isHoliday?: boolean;
  leaveType?: string;
  days?: number;
}

const Schedule: React.FC = () => {
  const { showToast } = useAlert();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [holidays, setHolidays] = useState<CalendarEvent[]>([]);
  const [leaveEvents, setLeaveEvents] = useState<CalendarEvent[]>([]);
  const { user, userRole } = useAuth();

  // PPE 모달 관련 상태
  const [isPPEModalOpen, setIsPPEModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // 공휴일 및 주말 데이터 로드
  useEffect(() => {
    const hd = new Holidays('KR'); // 한국 공휴일
    const year = currentDate.getFullYear();
    const holidayData = hd.getHolidays(year);
    
    // 공휴일 이벤트
    const holidayEvents: CalendarEvent[] = holidayData.map((holiday, index) => ({
      id: -1000 - index, // 음수로 설정하여 프로젝트 ID와 구분
      title: holiday.name,
      startDate: new Date(holiday.date),
      endDate: new Date(holiday.date),
      type: 'HOLIDAY',
      isHoliday: true
    }));
    
    // 주말 이벤트는 생성하지 않고 공휴일만 표시
    setHolidays(holidayEvents);
  }, [currentDate]);

  // 프로젝트 및 휴가 데이터 로드
  useEffect(() => {
    if (user?.id) {
      fetchMyProjects();
      fetchMyLeaves();
    }
  }, [user?.id]);

  // 내 휴가 데이터 가져오기
  const fetchMyLeaves = async () => {
    if (!user?.id) return;

    try {
      const myAllLeaves = await attendanceApi.getLeaveRequestsByEmployee(user.id);
      const myLeaves = myAllLeaves.filter(leave => leave.status === '승인');

      const leaveCalendarEvents: CalendarEvent[] = myLeaves.map(leave => ({
        id: leave.id,
        title: `${leave.type} (${leave.days}일)`,
        startDate: new Date(leave.startDate),
        endDate: new Date(leave.endDate),
        type: 'LEAVE',
        leaveType: leave.type,
        days: leave.days
      }));

      setLeaveEvents(leaveCalendarEvents);
    } catch (error) {
      console.error('휴가 데이터 불러오기 실패:', error);
    }
  };

  const fetchMyProjects = async () => {
    try {
      setLoading(true);
      // 백엔드에서 PM 또는 PPE 투입인력에 포함된 프로젝트를 1회 쿼리로 조회
      const myProjects = await projectApi.getMyProjects();

      const projectEvents: CalendarEvent[] = myProjects.map(project => ({
        id: project.id,
        title: project.name,
        startDate: new Date(project.startDate),
        endDate: new Date(project.endDate),
        type: 'PROJECT',
        status: project.status,
        client: project.client,
        pm: project.pm,
        project: project
      }));

      setEvents(projectEvents);
    } catch (error) {
      showToast('일정 데이터를 불러오는데 실패했습니다: ' + (error as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 달력 생성 함수
  const generateCalendar = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startCalendar = new Date(firstDay);
    startCalendar.setDate(startCalendar.getDate() - firstDay.getDay());
    
    const endCalendar = new Date(lastDay);
    endCalendar.setDate(endCalendar.getDate() + (6 - lastDay.getDay()));
    
    const calendar = [];
    const current = new Date(startCalendar);
    
    while (current <= endCalendar) {
      calendar.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return calendar;
  };

  // 특정 날짜의 이벤트 찾기 (프로젝트 + 공휴일 + 휴가)
  const getEventsForDate = (date: Date) => {
    const projectEvents = events.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      return date >= eventStart && date <= eventEnd;
    });

    const holidayEvents = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.startDate);
      return date.toDateString() === holidayDate.toDateString();
    });

    const myLeaveEvents = leaveEvents.filter(leave => {
      const leaveStart = new Date(leave.startDate);
      const leaveEnd = new Date(leave.endDate);
      return date >= leaveStart && date <= leaveEnd;
    });

    return [...projectEvents, ...holidayEvents, ...myLeaveEvents];
  };

  // 특정 날짜가 공휴일인지 확인
  const isHoliday = (date: Date) => {
    return holidays.some(holiday => {
      const holidayDate = new Date(holiday.startDate);
      return date.toDateString() === holidayDate.toDateString();
    });
  };

  // 날짜 포맷팅
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long'
    });
  };

  // 이벤트 타입별 색상 (프로젝트 상태별 + 공휴일 + 휴가)
  const getEventColor = (event: CalendarEvent) => {
    if (event.type === 'HOLIDAY') {
      return 'bg-red-100 text-red-800 border-red-200';
    }

    if (event.type === 'LEAVE') {
      return 'bg-purple-100 text-purple-800 border-purple-200';
    }

    // 프로젝트 상태별 색상
    switch (event.status) {
      case '진행중':
        return 'bg-green-100 text-green-800 border-green-200';
      case '완료':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case '계획':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // 이전/다음 달 네비게이션
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  // PPE(프로젝트 손익)는 PM 이상만 본다 — 화면 권한과 같은 기준 (#63)
  const canSeePPE =
    userRole === 'ADMIN' || userRole === 'DEVELOPER' || userRole === 'PROJECT_MANAGER';

  // PPE 모달 열기
  const handleProjectClick = (project: Project) => {
    if (!canSeePPE) return;
    setSelectedProject(project);
    setIsPPEModalOpen(true);
  };

  // PPE 모달 닫기
  const closePPEModal = () => {
    setSelectedProject(null);
    setIsPPEModalOpen(false);
  };

  // PPE 저장 핸들러 (읽기 전용이므로 저장 불가)
  const handlePPESave = async (updatedData: any) => {
    // 읽기 전용 모드에서는 저장하지 않음
    closePPEModal();
  };

  const calendar = generateCalendar(currentDate);
  const today = new Date();
  
  return (
    <main className="flex-1 overflow-y-auto p-6 lg:p-8">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800">내 일정</h1>
        <p className="text-slate-500 mt-1">참여 중인 프로젝트 일정을 확인할 수 있습니다.</p>
      </header>

      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-500">프로젝트 정보를 불러오는 중...</p>
        </div>
      ) : (
        <>
          {/* 캘린더 헤더 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-slate-800">
                  {formatDate(currentDate)}
                </h2>
                <div className="flex space-x-1">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    오늘
                  </button>
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-500">
                  총 {events.length}개 프로젝트 참여 중
                </span>
              </div>
            </div>

            {/* 캘린더 그리드 */}
            <div className="p-4">
              {/* 요일 헤더 */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
                  <div
                    key={day}
                    className={`p-3 text-center text-sm font-medium ${
                      index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-slate-600'
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* 캘린더 날짜 */}
              <div className="grid grid-cols-7 gap-1">
                {calendar.map((date, index) => {
                  const dayEvents = getEventsForDate(date);
                  const isToday = date.toDateString() === today.toDateString();
                  const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                  return (
                    <div
                      key={index}
                      className={`min-h-[120px] p-2 border border-slate-200 ${
                        isCurrentMonth ? 'bg-white' : 'bg-slate-50'
                      } ${isHoliday(date) ? 'bg-red-50 border-red-200' : ''} 
                      hover:bg-slate-50 transition-colors`}
                    >
                      <div className={`text-sm font-medium mb-2 ${
                        isToday 
                          ? 'bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center' 
                          : isCurrentMonth 
                            ? isWeekend || isHoliday(date)
                              ? 'text-red-600'
                              : 'text-slate-900'
                            : 'text-slate-400'
                      }`}>
                        {isToday ? (
                          <span>{date.getDate()}</span>
                        ) : (
                          date.getDate()
                        )}
                      </div>

                      {/* 이벤트 표시 */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map((event) => (
                          <div
                            key={event.id}
                            className="relative group"
                          >
                            <div
                              className={`px-2 py-1 rounded text-xs font-medium border truncate cursor-default ${getEventColor(event)}`}
                            >
                              {event.title}
                            </div>
                            {/* 커스텀 툴팁 */}
                            <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block z-50 pointer-events-none">
                              <div className="bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg max-w-xs">
                                {event.title}
                                {event.type === 'PROJECT' && event.pm && (
                                  <span className="text-slate-300 ml-1">({event.pm})</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="px-2 py-1 text-xs text-slate-500 bg-slate-100 rounded truncate">
                            +{dayEvents.length - 2}개 더
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 프로젝트 목록 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">참여 중인 프로젝트</h3>
            </div>
            <div className="divide-y divide-slate-200">
              {events.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="font-medium text-lg mb-1">참여 중인 프로젝트가 없습니다.</p>
                  <p className="text-sm">새로운 프로젝트에 참여하게 되면 여기에 표시됩니다.</p>
                </div>
              ) : (
                events.map((event) => (
                  <div
                    key={event.id}
                    className={`p-6 transition-colors ${canSeePPE ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
                    onClick={() => canSeePPE && event.project && handleProjectClick(event.project)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className={`font-semibold text-slate-900 text-lg mb-3 transition-colors ${canSeePPE ? 'hover:text-indigo-600' : ''}`}>
                          {event.title}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
                          <div className="flex items-center">
                            <span className="font-medium text-slate-700 w-16">PM</span>
                            <span>{event.pm}</span>
                          </div>
                          {event.project?.contractValue && (
                            <div className="flex items-center">
                              <span className="font-medium text-slate-700 w-16">계약금액</span>
                              <span className="font-semibold text-green-600">
                                {event.project.contractValue.toLocaleString()}만원
                              </span>
                            </div>
                          )}
                          <div className="flex items-center">
                            <span className="font-medium text-slate-700 w-16">시작일</span>
                            <span>{event.startDate.toLocaleDateString('ko-KR')}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium text-slate-700 w-16">종료일</span>
                            <span>{event.endDate.toLocaleDateString('ko-KR')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 휴가 목록 */}
          {leaveEvents.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">내 휴가 일정</h3>
              </div>
              <div className="divide-y divide-slate-200">
                {leaveEvents.map((leave) => (
                  <div key={leave.id} className="p-6 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {leave.leaveType}
                          </span>
                          <h4 className="font-semibold text-slate-900 text-lg">
                            {leave.days}일
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
                          <div className="flex items-center">
                            <span className="font-medium text-slate-700 w-16">시작일</span>
                            <span>{leave.startDate.toLocaleDateString('ko-KR')}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium text-slate-700 w-16">종료일</span>
                            <span>{leave.endDate.toLocaleDateString('ko-KR')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* PPE 모달 */}
      {isPPEModalOpen && selectedProject && (
        <NewProjectModal
          isOpen={isPPEModalOpen}
          onClose={closePPEModal}
          onSave={handlePPESave}
          project={selectedProject}
          readOnly={true}
        />
      )}
    </main>
  );
};

export default Schedule;